import { create } from "zustand";
import {
  type AnyChangeLedgerItem,
  CHANGE_TYPES_WITH_INSTANT_UNDO,
  ChangeType,
  RedoChange,
  UndoChange,
  UpdateClassesChange,
} from "./changeLedgerTypes";
import { useCodebaseStore } from "./codebaseStore";
import type { SignedInSessionResource } from "@clerk/types";
import axios from "axios";
import type { JsxElement } from "@/utils/jsxElementTypes";
import { useEffect } from "react";
import { toast } from "sonner";
import { getTreeElementLookupToSend } from "@/utils/iframeUtils";
import useActivityStore, { type ActivityItem } from "./activityStore";
import { ActivityChangeTypeToName } from "@/components/ActivityStream/ActivityStreamItem";
import { useIframeStore } from "./iframeStore";
import { getAxiosErorrMessage } from "@/utils/utils";
import { track } from "@/utils/events/tracker";

const START_HISTORY_CONSTANT = "START_HISTORY_CONSTANT";

const getDisplayNameForItem = (
  ledgerItem: AnyChangeLedgerItem,
  activities: ActivityItem[]
) => {
  if (!ledgerItem) {
    return "";
  }

  if (ledgerItem.type === ChangeType.UNKNOWN) {
    const matchingActivity = activities.find(
      (activity) => activity.id === ledgerItem.activityId
    );
    if (matchingActivity) {
      return ActivityChangeTypeToName[matchingActivity.change_type];
    }
  }

  return ledgerItem.changeName;
};

const applyChangeToStore = (
  change: AnyChangeLedgerItem,
  get: () => DesignCommandManagerStore,
  set: (
    updateFunction: (
      state: DesignCommandManagerStore
    ) => Partial<DesignCommandManagerStore>
  ) => void
) => {
  if (change.type === ChangeType.UPDATE_CLASSES) {
    const updateClassesChange = change as UpdateClassesChange;
    const codebaseId = updateClassesChange.changeFields.codebaseIdToUpdateClass;

    set((state) => {
      return {
        editingClassesByCodebaseId: {
          ...state.editingClassesByCodebaseId,
          [codebaseId]: {
            value: updateClassesChange.changeFields.newClasses,
            lastManualUpdateAt: new Date(),
          },
        },
      };
    });
  }
};

const undoChangeInStore = (
  changeToUndo: AnyChangeLedgerItem,
  get: () => DesignCommandManagerStore,
  set: (
    updateFunction: (
      state: DesignCommandManagerStore
    ) => Partial<DesignCommandManagerStore>
  ) => void
) => {
  if (changeToUndo.type === ChangeType.UPDATE_CLASSES) {
    const updateClassesChange = changeToUndo as UpdateClassesChange;
    const codebaseId = updateClassesChange.changeFields.codebaseIdToUpdateClass;

    set((state) => {
      return {
        editingClassesByCodebaseId: {
          ...state.editingClassesByCodebaseId,
          [codebaseId]: {
            value: updateClassesChange.changeFields.oldClasses,
            lastManualUpdateAt: new Date(),
          },
        },
      };
    });
  }
};

const redoChangeInStore = (
  change: AnyChangeLedgerItem,
  get: () => DesignCommandManagerStore,
  set: (
    updateFunction: (
      state: DesignCommandManagerStore
    ) => Partial<DesignCommandManagerStore>
  ) => void
) => {
  if (change.type === ChangeType.UPDATE_CLASSES) {
    // Simply apply the change again
    applyChangeToStore(change, get, set);
  }
};

interface DesignCommandManagerStore {
  canvasId: string;
  // Queue of changes waiting for server update
  serverChangeQueue: AnyChangeLedgerItem[];
  // Queue of changes waiting for instant update
  instantChangeQueue: AnyChangeLedgerItem[];

  undoStack: AnyChangeLedgerItem[];
  redoStack: AnyChangeLedgerItem[];

  setUndoStack: (undoStack: AnyChangeLedgerItem[]) => void;
  setRedoStack: (redoStack: AnyChangeLedgerItem[]) => void;

  // Whether we're currently processing a server change
  isProcessingServer: boolean;
  // Whether we're currently processing an instant change
  isProcessingInstant: boolean;
  // Timer for tracking empty queues
  emptyQueuesTimer: NodeJS.Timeout | null;

  // Clerk session
  session: SignedInSessionResource | null;

  // Submit a command that will be recorded a
  executeCommand: (designCommand: AnyChangeLedgerItem) => void;
  undo: () => void;
  redo: () => void;

  // Add a change to the instant queue
  sendChange: (change: AnyChangeLedgerItem) => void;
  sendInstantChange: (change: AnyChangeLedgerItem) => void;

  // Process the next server change
  processNextServerChange: () => Promise<void>;
  // Process the next instant change
  processNextInstantChange: () => Promise<void>;
  // Clear all queues
  clearQueues: () => void;
  // Flush changes to server
  flushChanges: () => Promise<boolean>;
  // Reset the store to initial state
  resetDesignCommandManagerStore: (canvasId: string) => void;

  // Set the clerk session
  setSession: (session: SignedInSessionResource | null) => void;

  isFlushing: boolean;
  setIsFlushing: (isFlushing: boolean) => void;

  // Editting classes by codebaseId, this is the source of truth, all edits happens here and are propagated
  // to backend/instant updates
  editingClassesByCodebaseId: {
    [codebaseId: string]: {
      value: string[];
      lastManualUpdateAt: Date | null;
    };
  };

  setEditingClassesByCodebaseId: (newEditingClassesByCodebaseId: {
    [codebaseId: string]: {
      value: string[];
      lastManualUpdateAt: Date | null;
    };
  }) => void;
}

const TIME_BEFORE_FLUSHING_CHANGES = 10_000;

export const useDesignCommandManager = create<DesignCommandManagerStore>(
  (set, get) => ({
    canvasId: "",
    isFlushing: false,
    serverChangeQueue: [],
    instantChangeQueue: [],
    isProcessingServer: false,
    isProcessingInstant: false,
    emptyQueuesTimer: null,
    session: null,
    editingClassesByCodebaseId: {},
    undoStack: [],
    redoStack: [],

    setIsFlushing: (isFlushing: boolean) => {
      set({ isFlushing });
    },

    setUndoStack: (undoStack: AnyChangeLedgerItem[]) => {
      set({ undoStack });
    },

    setRedoStack: (redoStack: AnyChangeLedgerItem[]) => {
      set({ redoStack });
    },

    setEditingClassesByCodebaseId: (newEditingClassesByCodebaseId: {
      [codebaseId: string]: {
        value: string[];
        lastManualUpdateAt: Date;
      };
    }) => {
      set({ editingClassesByCodebaseId: newEditingClassesByCodebaseId });
    },

    setSession: (session: SignedInSessionResource | null) => {
      set({ session });
    },

    resetDesignCommandManagerStore: (canvasId: string) => {
      // Clear any existing timer since we're adding new changes
      const timer = get().emptyQueuesTimer;
      if (timer) {
        clearTimeout(timer);
      }

      set({
        canvasId,
        serverChangeQueue: [],
        instantChangeQueue: [],
        isProcessingServer: false,
        isProcessingInstant: false,
        emptyQueuesTimer: null,
        editingClassesByCodebaseId: {},
        undoStack: [],
        redoStack: [],
      });
    },

    executeCommand: (change: AnyChangeLedgerItem) => {
      set((state) => ({
        undoStack: [...state.undoStack, change],
        redoStack: [],
      }));

      track(change.type);

      applyChangeToStore(change, get, set);
      get().sendChange(change);
    },

    undo: () => {
      const undoStack = [...get().undoStack];

      if (undoStack.length === 0) {
        return;
      }

      const changeToUndo = undoStack.pop()!;

      set((state) => ({
        redoStack: [...state.redoStack, changeToUndo],
        undoStack: undoStack,
      }));

      undoChangeInStore(changeToUndo, get, set);

      // There is a problem with current undo/redo, it leads to files sometimes being deleted
      // especially with rapid undo/redo. For styling changes, we simply apply the change in the reverse direction
      // instead of wrapping it in an UndoChange.
      let changeToSend: AnyChangeLedgerItem;
      if (changeToUndo.type === ChangeType.UPDATE_CLASSES) {
        const updateClassesChange = changeToUndo as UpdateClassesChange;
        changeToSend = new UpdateClassesChange({
          codebaseIdToUpdateClass:
            updateClassesChange.changeFields.codebaseIdToUpdateClass,
          newClasses: updateClassesChange.changeFields.oldClasses,
          oldClasses: updateClassesChange.changeFields.newClasses,
          involvedClasses: updateClassesChange.changeFields.involvedClasses,
        });
        changeToSend.asUndoOfActivityId = changeToUndo.activityId;
      } else {
        changeToSend = new UndoChange({
          changeToUndo,
        });
      }

      // Show progress toast for undo/redo
      const displayName = getDisplayNameForItem(
        changeToUndo,
        useActivityStore.getState().activityStreamList
      );
      if (!CHANGE_TYPES_WITH_INSTANT_UNDO.includes(changeToSend.type)) {
        const toastId = toast.loading(
          displayName ? `Undoing ${displayName}` : "Undoing Change"
        );
        changeToSend.toastId = toastId;

        setTimeout(() => {
          toast.dismiss(toastId);
        }, 10000);
      } else {
        toast.success(displayName ? `Undo: ${displayName}` : "Undo");
      }

      get().sendChange(changeToSend);
    },

    redo: () => {
      const redoStack = [...get().redoStack];
      if (redoStack.length === 0) {
        return;
      }

      const changeToRedo = redoStack.pop()!;
      set((state) => ({
        undoStack: [...state.undoStack, changeToRedo],
        redoStack: redoStack,
      }));

      redoChangeInStore(changeToRedo, get, set);

      let changeToSend: AnyChangeLedgerItem;

      // There is a problem with current undo/redo, it leads to files sometimes being deleted
      // especially with rapid undo/redo. For styling changes, we simply apply the change in the reverse direction
      // instead of wrapping it in an UndoChange.
      if (changeToRedo.type === ChangeType.UPDATE_CLASSES) {
        const updateClassesChange = changeToRedo as UpdateClassesChange;
        changeToSend = new UpdateClassesChange({
          codebaseIdToUpdateClass:
            updateClassesChange.changeFields.codebaseIdToUpdateClass,
          newClasses: updateClassesChange.changeFields.newClasses,
          oldClasses: updateClassesChange.changeFields.oldClasses,
          involvedClasses: updateClassesChange.changeFields.involvedClasses,
        });
        changeToSend.asRedoOfActivityId = changeToRedo.activityId;
      } else {
        changeToSend = new RedoChange({
          changeToRedo,
        });
      }

      const displayName = getDisplayNameForItem(
        changeToRedo,
        useActivityStore.getState().activityStreamList
      );
      if (!CHANGE_TYPES_WITH_INSTANT_UNDO.includes(changeToSend.type)) {
        const toastId = toast.loading(
          displayName ? `Redoing ${displayName}` : "Redoing Change"
        );
        changeToSend.toastId = toastId;

        setTimeout(() => {
          toast.dismiss(toastId);
        }, 10000);
      } else {
        toast.success(displayName ? `Redo: ${displayName}` : "Redo");
      }

      get().sendChange(changeToSend);
    },

    sendChange: (change: AnyChangeLedgerItem) => {
      // Clear any existing timer since we're adding new changes
      const timer = get().emptyQueuesTimer;
      if (timer) {
        clearTimeout(timer);
        set({ emptyQueuesTimer: null });
      }

      const isFlushing = get().isFlushing;

      const newServerChangeQueue = [...get().serverChangeQueue, change];

      let newInstantChangeQueue: AnyChangeLedgerItem[] = [
        ...get().instantChangeQueue,
      ];
      if (!change.canInstantUpdateWhileFlushing && isFlushing) {
        // Show a toast instead of instant update (if toast not already shown)
        if (!change.toastId) {
          const displayName = getDisplayNameForItem(
            change,
            useActivityStore.getState().activityStreamList
          );
          const toastId = toast.loading(
            displayName ? `Applying ${displayName}` : "Applying Change"
          );
          change.toastId = toastId;

          setTimeout(() => {
            toast.dismiss(toastId);
          }, 10000);
        }
      } else {
        newInstantChangeQueue.push(change);
        change.markInstantUpdateSent();
      }

      set({
        serverChangeQueue: newServerChangeQueue,
        instantChangeQueue: newInstantChangeQueue,
      });

      // Start processing both queues if they're not already processing
      if (!get().isProcessingInstant) {
        get().processNextInstantChange();
      }
      if (!get().isProcessingServer) {
        get().processNextServerChange();
      }
    },

    sendInstantChange: (change) => {
      const timer = get().emptyQueuesTimer;
      if (timer) {
        clearTimeout(timer);
        set({ emptyQueuesTimer: null });
      }

      set((state) => ({
        instantChangeQueue: [...state.instantChangeQueue, change],
      }));

      if (!get().isProcessingInstant) {
        get().processNextInstantChange();
      }
    },

    processNextServerChange: async () => {
      const state = get();
      if (state.isProcessingServer || state.serverChangeQueue.length === 0) {
        return;
      }

      set({ isProcessingServer: true });
      const nextChange = state.serverChangeQueue[0];

      const { canvasId, session } = get();
      const { treeElementLookup } = useCodebaseStore.getState();

      const changesInGroup: AnyChangeLedgerItem[] = [];

      try {
        // Submit to server
        // Note: The actual server submission should be handled by the change item's implementation

        const { urlPath, body, successToastMessage } =
          nextChange.prepareApiRequest(canvasId, treeElementLookup);

        if (nextChange.getInstantUpdateSent()) {
          // TODO: This will be fixed in the new canvas PR (Peter) - Aug 5, 2025, should be out in 1-2 weeks
          body.isInstantUpdate = false;
        }

        if (
          nextChange.type === ChangeType.UNDO ||
          nextChange.type === ChangeType.REDO
        ) {
          // Group with other undo/redo changes in the server queue
          let changeToGroupWith = nextChange;
          let activityIdToRestoreTo: string | undefined;

          let i = 0;
          while (
            changeToGroupWith &&
            (changeToGroupWith.type === ChangeType.UNDO ||
              changeToGroupWith.type === ChangeType.REDO)
          ) {
            changesInGroup.push(changeToGroupWith);

            if (changeToGroupWith.type === ChangeType.REDO) {
              activityIdToRestoreTo = (changeToGroupWith as RedoChange)
                .changeFields.changeToRedo.activityId;
            } else {
              activityIdToRestoreTo = (changeToGroupWith as UndoChange)
                .changeFields.changeToUndo.activityId;
            }

            changeToGroupWith = state.serverChangeQueue[i + 1];
            i++;
          }

          const activityToRestore =
            useActivityStore.getState().activityStreamLookup[
              activityIdToRestoreTo || ""
            ];

          if (!activityToRestore) {
            console.error("Undo/Redo activity ID not found");
            return;
          } else if (activityToRestore.change_undone) {
            body.redoRestoreActivityId = activityIdToRestoreTo;
          } else {
            // If undoing, based on the logic we need to restore to the activity before this one
            // If there are no activities before this one, we use the START_HISTORY_CONSTANT
            // Activity stream is newest to oldest, so we need to reverse it
            const reversedActivityStreamList = [
              ...useActivityStore.getState().activityStreamList,
            ].reverse();

            const lastActivityToUndoIndex =
              reversedActivityStreamList.findIndex(
                (activity) => activity.id === activityIdToRestoreTo
              );

            if (lastActivityToUndoIndex === 0) {
              body.undoRestoreActivityId = START_HISTORY_CONSTANT;
            } else {
              body.undoRestoreActivityId =
                reversedActivityStreamList[lastActivityToUndoIndex - 1].id;
            }
          }
        } else {
          changesInGroup.push(nextChange);

          if (nextChange.asUndoOfActivityId) {
            body.asUndoOfActivityId = nextChange.asUndoOfActivityId;
          } else if (nextChange.asRedoOfActivityId) {
            body.asRedoOfActivityId = nextChange.asRedoOfActivityId;
          }
        }

        body.expectedStepsCount = changesInGroup.length;

        const supabaseJWT = await session?.getToken();
        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/${urlPath}`,
          body,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        );
        if (response?.data) {
          useCodebaseStore
            .getState()
            .updateLookupsBasedOnPartialResponse(response.data);
        }

        if (response?.data?.warning) {
          toast.warning(response.data.warning);
        } else if (successToastMessage) {
          toast.success(successToastMessage);
        }

        changesInGroup.forEach((change) => {
          if (change.toastId) {
            toast.success("Success", { id: change.toastId });
            change.toastId = undefined;
          }

          change.resolveApi?.(response.data);
        });

        if (response?.data?.activityId) {
          nextChange.activityId = response.data.activityId;
        }

        // If there is info returned about codebase IDs that changed, update the iframe and the selected elements
        // Note - the update codebase IDs function inside devtools also updates the selected/hovered codebase IDs
        if (response?.data?.prevIdToNewIdMap) {
          // If the change was undone while the API was in flight, don't apply the new codebase ID changes
          if (!nextChange.changeUndone) {
            const { treeElementLookup, scopeLookup } =
              useCodebaseStore.getState();
            const treeElementToSend = treeElementLookup
              ? getTreeElementLookupToSend(treeElementLookup)
              : undefined;

            useIframeStore.getState().sendMessageToIFrame({
              type: "executeFunction",
              payload: {
                functionName: "updateCodebaseIds",
                args: [
                  response.data.prevIdToNewIdMap,
                  treeElementToSend,
                  scopeLookup,
                ],
              },
            });

            state.serverChangeQueue
              .slice(changesInGroup.length)
              .forEach((ledgerItem) => {
                ledgerItem.applyAllCodebaseIdChanges(
                  response.data.prevIdToNewIdMap
                );
              });
          }

          nextChange.prevIdToNewIdMap = response.data.prevIdToNewIdMap;
        }

        // This triggers selecting a particular codebase ID after changing
        if (response.data.selectCodebaseId && !nextChange.changeUndone) {
          useIframeStore
            .getState()
            .setPendingSelectionCodebaseId(response.data.selectCodebaseId);
        }
      } catch (error) {
        console.error("Error processing server change:", error);

        // Check for steps delta mismatch error (409 status with STEPS_DELTA_MISMATCH)
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.error === "STEPS_DELTA_MISMATCH"
        ) {
          toast.error(
            `${error.response.data.message} Refreshing activity stream...`
          );

          // Trigger activity stream refetch immediately
          useActivityStore.getState().triggerRefetchActivityStream();

          const newUndoStack = [...get().undoStack];
          const newRedoStack = [...get().redoStack];
          for (const change of changesInGroup) {
            if (change.toastId) {
              toast.error("Sync Error", { id: change.toastId });
              change.toastId = undefined;
            }

            // Put the change back into the undo/redo stack if it's an undo/redo change
            if (change.type === ChangeType.UNDO) {
              newRedoStack.pop();
              newUndoStack.push(
                (change as UndoChange).changeFields.changeToUndo
              );
            } else if (change.type === ChangeType.REDO) {
              newUndoStack.pop();
              newRedoStack.push(
                (change as RedoChange).changeFields.changeToRedo
              );
            }

            change.rejectApi?.(error);
          }

          set({
            undoStack: newUndoStack,
            redoStack: newRedoStack,
          });

          return; // Exit early for sync errors
        }

        // Handle other errors normally
        toast.error("Error: " + getAxiosErorrMessage(error));

        const newUndoStack = [...get().undoStack];

        const newRedoStack = [...get().redoStack];
        changesInGroup.forEach((change) => {
          if (change.toastId) {
            toast.error("Error", { id: change.toastId });
            change.toastId = undefined;
          }

          // Put the change back into the undo/redo stack if it's an undo/redo change
          if (change.type === ChangeType.UNDO) {
            newRedoStack.pop();
            newUndoStack.push((change as UndoChange).changeFields.changeToUndo);
          } else if (change.type === ChangeType.REDO) {
            newUndoStack.pop();
            newRedoStack.push((change as RedoChange).changeFields.changeToRedo);
          }

          change.rejectApi?.(error);
        });

        set({
          undoStack: newUndoStack,
          redoStack: newRedoStack,
        });
      } finally {
        // Remove the processed change from the server queue
        set((state) => ({
          serverChangeQueue: state.serverChangeQueue.slice(
            changesInGroup.length
          ),
          isProcessingServer: false,
        }));

        changesInGroup.forEach((change) => {
          if (change.toastId) {
            toast.dismiss(change.toastId);
            change.toastId = undefined;
          }
        });

        useActivityStore.getState().triggerRefetchActivityStream();

        // Check if both queues are empty and start timer if they are
        if (
          get().serverChangeQueue.length === 0 &&
          get().instantChangeQueue.length === 0
        ) {
          const timer = setTimeout(() => {
            get().flushChanges();
          }, TIME_BEFORE_FLUSHING_CHANGES);
          set({ emptyQueuesTimer: timer });
        }

        // Process next server change if any
        if (get().serverChangeQueue.length > 0) {
          get().processNextServerChange();
        }
      }
    },

    processNextInstantChange: async () => {
      const state = get();
      if (state.isProcessingInstant || state.instantChangeQueue.length === 0) {
        return;
      }

      set({ isProcessingInstant: true });
      const nextChange = state.instantChangeQueue[0];

      try {
        // Apply instant update
        useIframeStore.getState().sendMessageToIFrame({
          type: "executeFunction",
          payload: {
            functionName: "applyChangeItemToDocument",
            args: [nextChange],
          },
        });

        // If we are undoing or redoing, reapply the codebase Id changes to the rest of the ledger
        if (nextChange.type === ChangeType.UNDO) {
          // Apply the undone prev Id to new Id map to the ledger
          const reverseMap = {};
          for (const [key, value] of Object.entries(
            (nextChange as UndoChange)?.changeFields?.changeToUndo
              ?.prevIdToNewIdMap || {}
          )) {
            reverseMap[value] = key;
          }

          state.instantChangeQueue.slice(1).forEach((ledgerItem) => {
            ledgerItem.applyAllCodebaseIdChanges(reverseMap);
          });
        } else if (nextChange.type === ChangeType.REDO) {
          // Reapply the original prev id to new id map
          state.instantChangeQueue.slice(1).forEach((ledgerItem) => {
            ledgerItem.applyAllCodebaseIdChanges(
              (nextChange as RedoChange)?.changeFields?.changeToRedo
                ?.prevIdToNewIdMap || {}
            );
          });
        }
      } catch (error) {
        console.error("Error processing instant change:", error);
      } finally {
        // Remove the processed change from the instant queue
        set((state) => ({
          instantChangeQueue: state.instantChangeQueue.slice(1),
          isProcessingInstant: false,
        }));

        // Check if both queues are empty and start timer if they are
        if (
          get().serverChangeQueue.length === 0 &&
          get().instantChangeQueue.length === 0
        ) {
          const timer = setTimeout(() => {
            get().flushChanges();
          }, TIME_BEFORE_FLUSHING_CHANGES);
          set({ emptyQueuesTimer: timer });
        }

        // Process next instant change if any
        if (get().instantChangeQueue.length > 0) {
          get().processNextInstantChange();
        }
      }
    },

    clearQueues: () => {
      const timer = get().emptyQueuesTimer;
      if (timer) {
        clearTimeout(timer);
      }
      set({
        serverChangeQueue: [],
        instantChangeQueue: [],
        isProcessingServer: false,
        isProcessingInstant: false,
        emptyQueuesTimer: null,
      });
    },

    flushChanges: async () => {
      // Clear the timer
      set({ emptyQueuesTimer: null });

      const { canvasId, session } = get();

      const supabaseJWT = await session?.getToken();

      set({ isFlushing: true });

      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/flushChangesV2`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      useCodebaseStore.getState().updateLookupsBasedOnPartialResponse(res.data);

      set({
        editingClassesByCodebaseId: {},
        isFlushing: false,
      });

      return Object.keys(res?.data || {}).length > 0;
    },
  })
);

export const useEditingClassesByCodebaseId = (
  selectedTreeElement: JsxElement | null
) => {
  const editingClassesByCodebaseId = useDesignCommandManager(
    (state) => state.editingClassesByCodebaseId
  );
  const setEditingClassesByCodebaseId = useDesignCommandManager(
    (state) => state.setEditingClassesByCodebaseId
  );

  useEffect(() => {
    if (!selectedTreeElement) {
      return;
    }

    const editingClassesByCodebaseId =
      useDesignCommandManager.getState().editingClassesByCodebaseId;

    const currentValue:
      | { value: string[]; lastManualUpdateAt: Date | null }
      | undefined = editingClassesByCodebaseId[selectedTreeElement.codebaseId];

    if (!currentValue) {
      // Set the classes to be the same as in the code
      useDesignCommandManager.getState().setEditingClassesByCodebaseId({
        [selectedTreeElement.codebaseId]: {
          value: selectedTreeElement.removableClasses || [],
          lastManualUpdateAt: null,
        },
      });
    } else {
      // If the classes have not been manually editted in the last 10 seconds, reset them to be the same as in the code
      if (
        currentValue.lastManualUpdateAt &&
        currentValue.lastManualUpdateAt < new Date(Date.now() - 10_000)
      ) {
        useDesignCommandManager.getState().setEditingClassesByCodebaseId({
          ...editingClassesByCodebaseId,
          [selectedTreeElement.codebaseId]: {
            value: selectedTreeElement.removableClasses || [],
            lastManualUpdateAt: null,
          },
        });
      }
    }
  }, [selectedTreeElement]);

  if (!selectedTreeElement) {
    return {
      classes: [],
      setClasses: () => {},
    };
  }

  return {
    classes:
      editingClassesByCodebaseId[selectedTreeElement.codebaseId]?.value ||
      selectedTreeElement.removableClasses ||
      [],
    setClasses: (classes: string[]) => {
      setEditingClassesByCodebaseId({
        ...editingClassesByCodebaseId,
        [selectedTreeElement.codebaseId]: {
          value: classes,
          lastManualUpdateAt: new Date(),
        },
      });
    },
  };
};

export const useIsChangePending = () => {
  const isFlushing = useDesignCommandManager((state) => state.isFlushing);
  const serverChangeQueue = useDesignCommandManager(
    (state) => state.serverChangeQueue
  );
  const instantChangeQueue = useDesignCommandManager(
    (state) => state.instantChangeQueue
  );
  const isProcessingServer = useDesignCommandManager(
    (state) => state.isProcessingServer
  );
  const isProcessingInstant = useDesignCommandManager(
    (state) => state.isProcessingInstant
  );
  return (
    isFlushing ||
    serverChangeQueue.length > 0 ||
    instantChangeQueue.length > 0 ||
    isProcessingServer ||
    isProcessingInstant
  );
};
